'use client';

import * as Sentry from '@sentry/nextjs';
import { fetchProductList } from '@/api/product';
import { ListProduct } from '@/api/types/public';
import Button from '@/components/Button';
import RecommendList from '@/components/GuessYouLike';
import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { Box, useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from '@/components/Toast';
import logger from '@/utils/client-logger';

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
  // reset: () => void;
}) {
  // TODO:这里使用logger.error会导致日志重复输出，直到内存耗尽？？

  // console.error('Error boundary caught:', error);

  const [list, setList] = useState<ListProduct[]>([]);
  const currency = useStore((state) => state.currency);
  const isMobile = useMediaQuery('(max-width:1024px)');
  const t = useTranslations('error');
  const globalT = useTranslations('goodsDetail');
  const router = useRouter();

  /**
   * 上报客户端出现 500 的情况
   */
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // 监听网络状态变化
    const handleOnline = async () => {
      logger.info('[ClientPage] online');
      // 如果当前已经是联网状态（double check），再执行后续逻辑
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
      }

      // 1. 防抖：延迟 2 秒
      timer = setTimeout(async () => {
        try {
          // 2. 双重确认：发一个轻量请求探测（使用 favicon 或 health 接口）
          // 由于没有专门的 health 接口，这里请求根路径作为探测
          const res = await fetch('/', { method: 'HEAD', cache: 'no-store' });

          if (res.ok) {
            // 3. 提示用户
            toast.success({
              content: 'Network recovered. Reloading...',
              duration: 2000,
            });

            // 4. 软刷新 (router.refresh)
            // 注意：router.refresh() 会重新获取服务端数据，但如果 error.tsx 是因为服务端渲染失败（如 hydration error），
            // 有时软刷新可能不够，这里根据需求使用 router.refresh() 尝试无感恢复
            router.refresh();

            // 5. 增加兜底刷新：如果软刷新后 200ms 内页面状态没有恢复（依然在 error 组件中），则执行硬刷新
            // 注意：router.refresh() 是异步的，这里使用 setTimeout 做一个简单的兜底
            setTimeout(() => {
              window.location.reload();
            }, 200);
          }
        } catch {
          // 网络虽连上但无法访问互联网，静默失败，等待下次尝试
        }
      }, 2000);
    };

    // 如果组件挂载时已经在线（可能是 SSR 错误但网络没问题），是否需要尝试重试？
    // 目前逻辑仅在状态切换时触发

    const handleOffline = () => {
      logger.info('[ClientPage] offline');
      // 如果还没来得及刷新就又断网了，取消刷新
      if (timer) {
        clearTimeout(timer);
      }
    };

    logger.info('[ClientPage] addEventListener');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      logger.info('[ClientPage] removeEventListener');
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [router]);

  useEffect(() => {
    fetchProductList({
      currency: currency.name,
      group_id: 116,
      limit: 10,
      page: 1,
      in_stock: 1,
      is_sale: 1,
    }).then((res) => setList((res.data as any).list));
  }, [currency]);

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      {/* Top Banner Section - 对应 .top-img */}
      <div className="relative">
        <Image
          src="/errorBanner.png"
          alt={t('bannerImageAlt')}
          width={1920}
          height={400}
          className="w-full h-auto hidden md:block"
        />
        <Image
          src="/wapErrorBanner.png"
          alt={t('bannerImageAlt')}
          width={750}
          height={420}
          className="w-full h-auto block md:hidden"
        />
        {/* 对应 .words */}
        <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center">
          {/* 对应 p:nth-child(1) */}
          <p className="text-[36px] lg:text-[72px] text-[#14c7ff] font-bb">500</p>
          {/* 对应 p:nth-child(2) */}
          <p className="text-[15px] lg:text-[36px] text-primary-purple font-bb mt-[0px] lg:mt-[20px] mb-[20px] lg:mb-[30px]">
            {t('title')}
          </p>
          {/* 对应 .el-button */}
          <Link prefetch={false} href="/">
            <Button
              variant="contained"
              color="primary"
              className="h-[25px] lg:h-[60px]  text-white"
              sx={{
                fontSize: isMobile ? '12px' : '20px',
                fontFamily: 'var(--font-bricolage-bold)',
                borderRadius: isMobile ? '5px' : '10px',
              }}
            >
              {t('button')}
            </Button>
          </Link>
        </div>
      </div>

      {/* You May Also Like Section - 对应 .bottom-list */}
      <Box className="py-[22px] lg:py-[70px] px-[12px] lg:px-[95px] text-center bg-[#f5f5f5]">
        <RecommendList
          list={list}
          title={globalT('recommendProducts.title')}
          renderTitle={(title) => (
            <p className="mb-[16px] lg:mb-[35px] text-[14px] lg:text-[36px] font-bold">{title}</p>
          )}
        />
      </Box>
    </div>
  );
}
